import { useState, useEffect } from "react";

interface VenueDetails {
  minParticipants: number;
  maxParticipants: number;
  name: string | null;
  description: string | null;
  languages: string[];
  isLoading: boolean;
  error: string | null;
}

export const useVenueDetails = (venueId: string | null): VenueDetails => {
  const [venueDetails, setVenueDetails] = useState<VenueDetails>({
    minParticipants: 1,
    maxParticipants: 100,
    name: null,
    description: null,
    languages: ["en"],
    isLoading: true, // Start with loading true to prevent flash
    error: null,
  });

  useEffect(() => {
    if (!venueId) {
      setVenueDetails((prev) => ({ ...prev, isLoading: false, error: null }));
      return;
    }

    const fetchVenueDetails = async () => {
      setVenueDetails((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        console.log("🔄 useVenueDetails: Fetching venue details for", venueId);

        const response = await fetch(`/api/venues/${venueId}/details`);
        const data = await response.json();

        if (data.success && data.data) {
          const details: VenueDetails = {
            minParticipants: data.data.min_participant || 1,
            maxParticipants: data.data.max_participant || 100,
            name: data.data.name || null,
            description: data.data.description || null,
            languages: data.data.languages || ["en"],
            isLoading: false,
            error: null,
          };

          setVenueDetails(details);

          console.log("✅ useVenueDetails: Loaded venue details", details);
        } else {
          throw new Error(data.error || "Failed to fetch venue details");
        }
      } catch (err) {
        console.error("❌ useVenueDetails: Fetch error:", err);
        const error =
          err instanceof Error ? err.message : "Failed to load venue details";
        setVenueDetails((prev) => ({ ...prev, isLoading: false, error }));
      }
    };

    fetchVenueDetails();
  }, [venueId]);

  return venueDetails;
};
